import { useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ExternalLink } from "lucide-react";
import { Link, useLocation } from "react-router";

import {
  externalNavAriaLabel,
  isPrimaryNavActive,
  PRIMARY_NAV,
} from "./primaryNav";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();
  const isPwa = window.matchMedia("(display-mode: standalone)").matches;
  const isWebiOS = /iPad|iPod|iPhone/.test(window.navigator.userAgent);

  return (
    <nav
      aria-label={translate("palmtree.nav.main")}
      className="fixed bottom-0 left-0 right-0 z-50 bg-nav"
      style={{
        paddingBottom: isPwa && isWebiOS ? 15 : undefined,
        height:
          "calc(var(--spacing) * 18)" + (isPwa && isWebiOS ? " + 15px" : ""),
      }}
    >
      <div className="flex">
        {PRIMARY_NAV.map((item) => {
          const label = translate(item.labelKey);
          return (
            <NavigationButton
              key={item.to}
              href={item.to}
              Icon={item.icon}
              label={label}
              ariaLabel={
                item.external
                  ? externalNavAriaLabel(label, translate)
                  : undefined
              }
              isActive={isPrimaryNavActive(item, location.pathname)}
              external={item.external}
            />
          );
        })}
      </div>
    </nav>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  ariaLabel,
  isActive,
  external = false,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  ariaLabel?: string;
  isActive: boolean;
  external?: boolean;
}) => {
  const content = (
    <>
      <span
        className={cn(
          "flex size-10 items-center justify-center rounded-row",
          isActive ? "bg-card shadow-card" : undefined,
        )}
      >
        <Icon
          className={cn(
            "size-5",
            isActive ? "text-primary" : "text-muted-foreground",
          )}
          strokeWidth={1.75}
        />
      </span>
      <span
        className={cn(
          "inline-flex max-w-full items-center justify-center gap-0.5 text-center text-[10px] leading-tight",
          isActive
            ? "font-[650] text-foreground"
            : "font-medium text-muted-foreground",
        )}
      >
        {label}
        {external ? (
          <ExternalLink aria-hidden="true" className="size-3" />
        ) : null}
      </span>
    </>
  );

  return (
    <Button
      asChild
      variant="ghost"
      // No min-w-0: five items at 360px would clip "Contactpersonen".
      className="flex-1 flex-col gap-0.5 h-auto py-1.5 px-0.5 rounded-md"
    >
      {external ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={ariaLabel}
        >
          {content}
        </a>
      ) : (
        <Link to={href} aria-current={isActive ? "page" : undefined}>
          {content}
        </Link>
      )}
    </Button>
  );
};
